import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Tag, BookOpen, Plus, Check, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { api, thumbUrl } from '../lib/api';
import { t } from '../lib/i18n';
import { useShoppingList } from '../context/ShoppingListContext';

export const useProductSearch = (query) => {
  const [state, setState] = useState({ loading: false, products: [] });
  useEffect(() => {
    if (!query || query.length < 2) { setState({ loading: false, products: [] }); return undefined; }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      api.get('/search', { params: { q: query } })
        .then((r) => alive && setState({ loading: false, products: r.data.products || [] }))
        .catch(() => alive && setState({ loading: false, products: [] }));
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);
  return state;
};

export const productLink = (p) => `/catalogue/${p.catalog_id}?page=${p.page}${p.box ? `&focus=${p.box.join(',')}` : ''}`;

export const CheaperBadge = () => null; // cross-store price comparison disabled (owner request)
const _CheaperBadgeLegacy = ({ cheaper, testId = 'cheaper-badge' }) => {
  if (!cheaper) return null;
  return (
    <Link to={productLink(cheaper)} onClick={(e) => e.stopPropagation()} data-testid={testId} title={`${cheaper.name} — ${cheaper.price}`}
      className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] font-bold leading-tight hover:bg-emerald-500/25 transition-colors">
      <TrendingDown size={11} className="shrink-0" /> <span>{t('Moins cher chez')} {cheaper.brand} · {cheaper.price}</span>
      {cheaper.saving_pct > 0 && <span className="text-emerald-400 shrink-0">(−{cheaper.saving_pct}%)</span>}
    </Link>
  );
};

export const AddToListButton = ({ p, testId = 'add-to-list' }) => {
  const list = useShoppingList();
  const added = list?.has(p);
  const onClick = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!list || added) return;
    try { await list.add(p); toast.success(`${p.name} — ${t('ajouté à votre liste de courses')}`, { action: { label: t('Voir ma liste'), onClick: () => { window.location.assign('/liste'); } } }); }
    catch { toast.error(t('Impossible d\'ajouter ce produit')); }
  };
  return (
    <button type="button" onClick={onClick} aria-label={added ? t('Dans votre liste de courses') : t('Ajouter à la liste de courses')} aria-pressed={added} data-testid={testId}
      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all active:scale-90 ${added ? 'bg-emerald-500 text-black' : 'bg-white/10 text-white hover:bg-[#E50914]'}`}>
      {added ? <Check size={15} /> : <Plus size={16} />}
    </button>
  );
};

export const ProductCard = ({ p, i, testId = 'product-result', compact = false }) => {
  const navigate = useNavigate();
  return (
    <div role="button" tabIndex={0} onClick={() => navigate(productLink(p))} onKeyDown={(e) => e.key === 'Enter' && navigate(productLink(p))} data-testid={`${testId}-${i}`}
      className="group flex items-center gap-3 text-left rounded-md border border-[#262626] bg-[#141414] p-2.5 hover:border-[#E50914] hover:bg-[#1a1a1a] transition-colors w-full cursor-pointer">
      {p.image && <img src={thumbUrl(p.image)} alt="" className="w-11 h-16 object-cover rounded border border-[#262626] shrink-0" loading="lazy" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white truncate" data-testid={`${testId}-name-${i}`}>{p.name}</p>
        {p.price && <p className="text-sm font-black text-[#E50914]">{p.price}</p>}
        <p className="text-xs text-[#808080] truncate flex items-center gap-1"><BookOpen size={11} /> {p.brand} · {t('page')} {p.page}</p>
        {p.cheaper && <div className="mt-1"><CheaperBadge cheaper={p.cheaper} testId={`${testId}-cheaper-${i}`} /></div>}
      </div>
      {!compact && <span className="hidden sm:inline text-[11px] font-semibold text-[#808080] group-hover:text-white whitespace-nowrap">{p.box ? t('Zoom sur le prix') : `${t('Ouvrir à la page')} ${p.page}`} →</span>}
      <AddToListButton p={p} testId={`${testId}-add-${i}`} />
    </div>
  );
};

const ProductResults = ({ query, loading, products }) => {
  if (!loading && products.length === 0) return null;
  return (
    <section className="mb-8" data-testid="product-results">
      <h2 className="text-base md:text-lg font-bold text-white mb-3 flex items-center gap-2 flex-wrap">
        <Tag size={18} className="text-[#E50914]" /> {t('Produits trouvés')}
        {loading ? <Loader2 size={14} className="animate-spin text-[#808080]" /> : <span className="text-sm font-semibold text-[#808080]">({products.length})</span>}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {products.map((p, i) => <ProductCard key={`${p.catalog_id}-${p.page}-${i}`} p={p} i={i} />)}
      </div>
    </section>
  );
};

export default ProductResults;
